"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, PieChart, Building2, Tag, Landmark, List, TrendingUp, LogOut, Settings } from "lucide-react";
import { logout } from "@/app/(auth)/actions";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { getCurrentUser } from "@/app/actions";
import { stopImpersonating } from "@/app/admin/actions";
import { ShieldAlert } from "lucide-react";

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [user, setUser] = useState<{ isAdminImpersonating?: boolean } | null>(null);

  useEffect(() => {
    getCurrentUser().then((u: any) => setUser(u));
  }, []);

  const isAuthPage = pathname?.startsWith("/login") || pathname?.startsWith("/register") || pathname?.startsWith("/onboarding") || pathname?.startsWith("/admin");
  if (isAuthPage) return null;

  const handleStopImpersonating = async () => {
    await stopImpersonating();
    router.push("/admin");
    router.refresh();
  };

  const handleLogout = async () => {
    await logout();
    router.push("/login");
  };

  const links = [
    { href: "/", label: "Overview", icon: Home },
    { href: "/transactions", label: "History", icon: List },
    { href: "/accounts", label: "Accounts", icon: Landmark },
    { href: "/budget", label: "Budget", icon: PieChart },
    { href: "/properties", label: "Properties", icon: Building2 },
    { href: "/investments", label: "Invest", icon: TrendingUp },
    { href: "/categories", label: "Categories", icon: Tag },
    { href: "/settings", label: "Settings", icon: Settings },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-20 w-full items-center overflow-x-auto no-scrollbar border-t border-border bg-background pb-safe pt-2 md:hidden shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)] px-4 gap-6">
      {links.map((item) => {
        const isActive = pathname === item.href;

        return (
          <Link
            key={item.label}
            href={item.href}
            className={`flex flex-col items-center justify-center space-y-1 min-w-[72px] shrink-0 transition-colors ${isActive ? "text-primary" : "text-secondary-foreground/60 hover:text-foreground"
              }`}
          >
            <item.icon className="h-5 w-5" />
            <span className="text-[10px] font-medium">{item.label}</span>
            {isActive && (
              <span className="absolute bottom-1 h-1 w-1 rounded-full bg-primary" />
            )}
          </Link>
        );
      })}
      
      {/* Sign Out Button */}
      <button
        onClick={handleLogout}
        className="flex flex-col items-center justify-center space-y-1 min-w-[72px] shrink-0 text-secondary-foreground/60 hover:text-danger transition-colors"
      >
        <LogOut className="h-5 w-5" />
        <span className="text-[10px] font-medium">Logout</span>
      </button>

      {user?.isAdminImpersonating && (
        <div className="fixed bottom-24 left-4 right-4 z-[60] bg-danger text-white p-3 rounded-2xl shadow-lg shadow-danger/20 flex items-center justify-between animate-in slide-in-from-bottom-5">
            <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-wider">Impersonation Mode</span>
            </div>
            <button 
                onClick={handleStopImpersonating}
                className="bg-white text-danger px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tight shadow-sm hover:bg-white/90"
            >
                Return to Admin
            </button>
        </div>
      )}
    </nav>
  );
}
